import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './LastCalculations.translations.json';
import React, { FC } from 'react';
import { DashboardPanel } from '../DashboardPanel';
import { useListLastCalculationsQuery } from './query/useGetLastCalculationsQuery';
import { Flex } from 'antd';
import {
  colorAccent1,
  colorGray,
  colorGreen,
  colorInfo,
  colorLabel,
  colorSuccess,
  colorWarning,
} from '@atlas-frontend/design-tokens';
import * as Icon from 'react-feather';
import styled from 'styled-components';
import { InfoIconUrl, TextEllipsis, Tooltip } from '../../common/components';
import {
  LastCalculationsItemType,
  LastCalculationsItemValidationType,
} from './schema/types';

const { addTranslations, t } = i18nNsFactory('last-calculations');
addTranslations(translations);

export const LastCalculations = () => {
  const { data: lastCalculations = {}, isLoading } =
    useListLastCalculationsQuery();

  return (
    <DashboardPanel
      title={t('title')}
      infoIconUrl={InfoIconUrl.LAST_CALCULATIONS}
      isLoading={isLoading}
      showMaximize={false}
    >
      <Flex vertical gap="small">
        <LastCalculationsRow
          label={t('inputs.label')}
          loading={lastCalculations.lastInputLoadings}
        />
        <LastCalculationsRow
          label={t('outputs.label')}
          loading={lastCalculations.lastOutputLoadings}
          showLoadingValidations={true}
        />
        <LastCalculationsRow
          label={t('reports.label')}
          loading={lastCalculations.lastReportLoadings}
          showLoadingValidations={true}
        />
      </Flex>
    </DashboardPanel>
  );
};

export const LastCalculationsRow: FC<{
  label: string;
  loading?: LastCalculationsItemType;
  showLoadingValidations?: boolean;
}> = ({ label, loading, showLoadingValidations }) => {
  return (
    <Flex gap="middle">
      <strong style={{ width: '60px' }}>{label}: </strong>
      <LastCalculationsLoading
        icon={
          <Icon.CheckCircle
            size={18}
            color={
              loading?.lastLoadingOks?.length ? colorGreen[6] : colorGray[2]
            }
          />
        }
        items={loading?.lastLoadingOks}
      />
      <LastCalculationsLoading
        icon={
          <Icon.AlertTriangle
            size={18}
            color={loading?.lastLoadingInfos?.length ? colorInfo : colorGray[2]}
          />
        }
        items={loading?.lastLoadingInfos}
      />
      <LastCalculationsLoading
        icon={
          <Icon.AlertTriangle
            size={18}
            color={
              loading?.lastLoadingErrors?.length ? colorAccent1 : colorGray[2]
            }
          />
        }
        items={loading?.lastLoadingErrors}
      />
      {showLoadingValidations && (
        <LastCalculationsLoadingValidations
          items={loading?.lastLoadingValidations}
        />
      )}
    </Flex>
  );
};

export const LastCalculationsLoading: FC<{
  icon: React.ReactNode;
  items?: string[];
}> = ({ icon, items }) => {
  const hasItems = items?.length;
  const item = (
    <Flex
      gap="small"
      style={{ width: '50px', color: hasItems ? colorLabel : colorGray[2] }}
    >
      {icon}
      {items?.length || 0}
    </Flex>
  );

  return hasItems ? (
    <Tooltip title={<LastCalculationsLoadingTooltip items={items} />}>
      {item}
    </Tooltip>
  ) : (
    item
  );
};

export const LastCalculationsLoadingTooltip: FC<{
  items?: string[];
}> = ({ items }) => {
  return (
    <Flex vertical>
      <TextEllipsis rows={16} renderTooltip={false}>
        {(items || []).map((item) => {
          return <div key={item}>{item}</div>;
        })}
      </TextEllipsis>
    </Flex>
  );
};

export const LastCalculationsLoadingValidations: FC<{
  items?: LastCalculationsItemValidationType[];
}> = ({ items }) => {
  const hasItems = items?.length;
  const item = (
    <Flex gap="small" style={{ color: hasItems ? colorLabel : colorGray[2] }}>
      <IconText color={hasItems ? colorLabel : colorGray[2]}>VR</IconText>
      {items?.length || 0}
    </Flex>
  );

  return hasItems ? (
    <Tooltip
      title={<LastCalculationsLoadingValidationsTooltip items={items} />}
    >
      {item}
    </Tooltip>
  ) : (
    item
  );
};

export const LastCalculationsLoadingValidationsTooltip: FC<{
  items?: LastCalculationsItemValidationType[];
}> = ({ items }) => {
  return (
    <Flex vertical gap="small">
      <TextEllipsis rows={16} renderTooltip={false}>
        {(items || []).map((item) => {
          return (
            <Flex gap="small" key={item.label}>
              <IconText
                color={getValidationErrorLevelColor(item.validationErrorLevel)}
              >
                VR
              </IconText>
              {item.label}
            </Flex>
          );
        })}
      </TextEllipsis>
    </Flex>
  );
};

export function getValidationErrorLevelColor(level: string | null) {
  switch (level) {
    case 'ERROR':
    case 'TECH_ERROR':
      return colorAccent1;
    case 'WARNING':
      return colorWarning;
    case 'INFO':
      return colorInfo;
    case 'SUCCESS':
      return colorSuccess;
    case 'NO_RULES':
      return colorGray[5];
    default:
      return colorLabel;
  }
}

export const IconText = styled.span<{ color?: string }>`
  color: ${({ color = colorLabel }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ color = colorLabel }) => color};
  height: 20px;
  border-radius: 50%;
  background-color: white;
  flex-shrink: 0;
`;
